import resolveRulebookListCardStatus from "@next-platform/client-common-core/rulebook/cards/resolveRulebookListCardStatus";
import { RulebookListCardStatus } from "@next-platform/client-common-core/rulebook/cards/RulebookListCardStatus";
import type { RulebookDraft } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import { type RulebookSummary, toMeterChips } from "@next-platform/packages-ts-types";
import { useMemo } from "react";

import { SortOptions } from "@/components/type";

/**
 * One row per Rulebook in "My Rulebooks": a saved Rulebook (opening its linked local draft when one exists)
 * or a local draft whose Rulebook isn't in the saved list. `draft` rides along on saved rows so their menu
 * can offer draft actions (export, duplicate, delete).
 */
export type MyRulebookEntry =
  | { kind: "draft"; key: string; name: string; updatedAt: number; draft: RulebookDraft }
  | { kind: "saved"; key: string; name: string; updatedAt: number; summary: RulebookSummary; draft?: RulebookDraft };

const nameOf = (value: unknown): string => (typeof value === "string" ? value : "");

const matchesQuery = (entry: MyRulebookEntry, query: string): boolean => {
  if (!query) {
    return true;
  }
  const description =
    entry.kind === "saved" ? (entry.summary.description ?? entry.draft?.definition.description) : entry.draft.definition.description;
  const chips =
    entry.kind === "saved"
      ? (entry.summary.meter_chips ?? (entry.draft ? toMeterChips(entry.draft.definition.meters) : [])).map((chip) => chip.label)
      : toMeterChips(entry.draft.definition.meters).map((chip) => chip.label);
  const searchValues = [entry.name, description ?? "", ...chips];
  return searchValues.some((value) => value.toLowerCase().includes(query));
};

/**
 * Drafts and saved Rulebooks as one list (as My Characters merges local drafts with saved chatbots), newest
 * first, filtered by name, description, and visible meter labels. A saved Rulebook folds in the draft carrying
 * its id in `rulebook_id` (the row opens the draft in the Editor); a draft whose link names a Rulebook not in
 * the saved list still shows as a draft.
 */
export default function useMyRulebookEntries({
  drafts,
  savedRulebooks,
  search,
  sort,
}: {
  drafts: RulebookDraft[];
  savedRulebooks: RulebookSummary[] | null;
  search: string;
  sort: SortOptions;
}): { entries: MyRulebookEntry[]; total: number } {
  // The link lives in the draft record, so the subscribed `drafts` list refolds the rows on any relink.
  return useMemo(() => {
    const linkedDraftIds = new Set<string>();
    const draftByRulebookId = new Map(drafts.filter((stored) => stored.rulebook_id !== undefined).map((stored) => [stored.rulebook_id!, stored]));
    const savedEntries: MyRulebookEntry[] = (savedRulebooks ?? []).map((summary) => {
      const draft = draftByRulebookId.get(summary.id);
      if (draft) {
        linkedDraftIds.add(draft.definition.id);
      }
      return {
        kind: "saved",
        key: `saved:${summary.id}`,
        name: summary.name,
        // Fold in the linked draft's timestamp so local edits keep the card on top.
        updatedAt: draft
          ? Math.max(summary.updatedAt ?? summary.createdAt ?? 0, Date.parse(draft.updated_at) || 0)
          : (summary.updatedAt ?? summary.createdAt ?? 0),
        summary,
        ...(draft ? { draft } : {}),
      };
    });
    const draftEntries: MyRulebookEntry[] = drafts
      .filter((draft) => !linkedDraftIds.has(draft.definition.id))
      .map((draft) => ({
        kind: "draft",
        key: `draft:${draft.definition.id}`,
        name: nameOf(draft.definition.name),
        updatedAt: Date.parse(draft.updated_at) || 0,
        draft,
      }));
    const all = [...draftEntries, ...savedEntries].sort((first, second) => second.updatedAt - first.updatedAt);
    // Action-required cards surface first so a sent-back Rulebook is never buried under newer saves.
    const isNeedsChanges = (entry: MyRulebookEntry) =>
      entry.kind === "saved" && resolveRulebookListCardStatus({ summary: entry.summary }) === RulebookListCardStatus.NEEDS_CHANGES;
    const needsChanges = all.filter(isNeedsChanges);
    const rest = all.filter((entry) => !isNeedsChanges(entry));
    if (sort === SortOptions.OLDEST) {
      needsChanges.reverse();
      rest.reverse();
    }
    const query = search.trim().toLowerCase();
    const matching = [...needsChanges, ...rest].filter((entry) => matchesQuery(entry, query));
    return { entries: matching, total: all.length };
  }, [drafts, savedRulebooks, search, sort]);
}
